import { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useImageUrl } from '../db/useImageUrl';
import './Lightbox.css';

/**
 * Over the whole window, not inside the panel: 420px is not enough of a photo
 * to decide anything by, which is the only reason to open one.
 *
 * A native `<dialog>`, so the focus trap, the top layer and Escape come from
 * the platform. The panel's own Escape never sees it.
 *
 * Which photo is showing belongs to the panel, not to this — the frame and
 * the lightbox are one selection at two sizes. Nothing here has to guard
 * against a photo being removed underneath it; the panel stops rendering it.
 */
export function Lightbox({
  imageIds,
  index,
  onIndex,
  onClose,
}: {
  /** The cover first, then the gallery — the order they appear in the panel. */
  imageIds: readonly string[];
  index: number;
  onIndex: (next: number) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const imageId = imageIds[index];
  const url = useImageUrl(imageId, 'full');

  useEffect(() => {
    const node = ref.current;
    node?.showModal();
    return () => node?.close();
  }, []);

  function step(by: number) {
    const next = index + by;
    if (next >= 0 && next < imageIds.length) onIndex(next);
  }

  return (
    <dialog
      ref={ref}
      className="lightbox"
      aria-label="Photo"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // The backdrop is the element itself; the image sits in the figure.
        if (event.target === ref.current) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') step(-1);
        if (event.key === 'ArrowRight') step(1);
        if (event.key === 'Escape') {
          // `onCancel` covers the platform's own close request; this covers
          // the panel's Escape handler, which would otherwise see the same
          // press and close the panel behind the photo.
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="lightbox__bar">
        <span className="lightbox__count">
          {index + 1} / {imageIds.length}
        </span>
        <button type="button" className="lightbox__icon" aria-label="Close photo" onClick={onClose}>
          <X size={20} aria-hidden />
        </button>
      </div>

      <figure className="lightbox__figure">
        {url !== undefined && <img className="lightbox__image" src={url} alt="" />}
      </figure>

      <div className="lightbox__nav">
        <button
          type="button"
          className="lightbox__icon"
          aria-label="Previous photo"
          disabled={index === 0}
          onClick={() => step(-1)}
        >
          <ChevronLeft size={20} aria-hidden />
        </button>
        <button
          type="button"
          className="lightbox__icon"
          aria-label="Next photo"
          disabled={index >= imageIds.length - 1}
          onClick={() => step(1)}
        >
          <ChevronRight size={20} aria-hidden />
        </button>
      </div>
    </dialog>
  );
}
